import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Typography, Progress, Space, Table, Modal, Upload } from 'antd';
import { showMessage } from '@/utils/notificationHelper';
import { SoundOutlined, PauseOutlined, CameraOutlined, UploadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { selectCurrentAdmin } from '@/redux/auth/selectors';

const { Title, Text } = Typography;
const { Dragger } = Upload;

const GlobalEnergyModal = () => {
    const dispatch = useDispatch();
    const audioRef = useRef(null);
    const playStartTime = useRef(null);
    const startTimeoutRef = useRef(null);
    const imageModalTimeoutRef = useRef(null);
    const autoStopTimeoutRef = useRef(null);
    const delayedModalTimeoutRef = useRef(null);
    const nextCycleTimeoutRef = useRef(null);
    const reminderTimeoutRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [stopLogs, setStopLogs] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [hasFirstStop, setHasFirstStop] = useState(false);
    const [sessionStartTime, setSessionStartTime] = useState(null);
    const [productivitySlots, setProductivitySlots] = useState([]);
    const [pastedImage, setPastedImage] = useState(null);
    const [isMarkedOut, setIsMarkedOut] = useState(false);
    const [imageModalCountdown, setImageModalCountdown] = useState(3);
    const [lastLogTime, setLastLogTime] = useState(null);
    const energySoundState = JSON.parse(localStorage.getItem('energySoundState'))
    const [currentSlot, setCurrentSlot] = useState(energySoundState?.currentSlot || 1)
    const [imageModalOpenTime, setImageModalOpenTime] = useState(null);
    // Get admin info from Redux
    const currentAdmin = useSelector(selectCurrentAdmin);

    // Load persisted state on mount
    useEffect(() => {
        loadPersistedState();
        return () => {
            clearAllIntervals();
        };
    }, []);

    // Auto-start the cycle when component loads if not marked out
    useEffect(() => {
        setTimeout(() => {
            if (!isMarkedOut) {
                console.log('Initiating cycle on mount');
                startNormalCycle();
            }
        }, 1000);
    }, [isMarkedOut]);

    // Recover ongoing playback if refreshed during alert
    // useEffect(() => {
    //     const recoverPlayback = () => {
    //         const storedStart = localStorage.getItem("playStartTime");
    //         if (storedStart && audioRef.current) {
    //             console.log('Recovering playback from stored start time');
    //             const startTime = parseInt(storedStart, 10);
    //             playStartTime.current = startTime;
    //             setIsPlaying(true);
    //             setShowModal(true);
    //             setElapsed(Math.floor((Date.now() - startTime) / 1000));
    //             audioRef.current.currentTime = 0;
    //             audioRef.current.volume = 1.0;
    //             audioRef.current.loop = true;
    //             audioRef.current.play().catch(err => {
    //                 console.warn('Recovery play failed:', err);
    //                 setIsPlaying(false);
    //                 setShowModal(false);
    //                 startNormalCycle();
    //             });
    //             const remaining = 10000 - (Date.now() - startTime);
    //             if (remaining > 0) {
    //                 autoStopTimeoutRef.current = setTimeout(() => handleStop('Auto'), remaining);
    //             } else {
    //                 handleStop('Auto');
    //             }
    //         } else {
    //             console.log('No playback to recover, starting normal cycle');
    //             startNormalCycle();
    //         }
    //     };

    //     if (audioRef.current) {
    //         recoverPlayback();
    //     }
    // }, [audioRef]);


    // Handle paste events globally when image modal is open
    useEffect(() => {
        const handlePaste = (e) => {
            if (!showImageModal) return;

            const items = e.clipboardData?.items;
            if (items) {
                for (let item of items) {
                    if (item.type.indexOf('image') !== -1) {
                        const file = item.getAsFile();
                        if (file) {
                            console.log('Image pasted for slot:', currentSlot);
                            setPastedImage(URL.createObjectURL(file));
                            processImage(file, 'pasted');
                        }
                    }
                }
            }
        };

        document.addEventListener('paste', handlePaste);
        return () => {
            document.removeEventListener('paste', handlePaste);
        };
    }, [showImageModal]);

    // Image modal countdown and auto-close
    useEffect(() => {
        let countdownInterval = null;

        if (showImageModal) {
            setImageModalCountdown(300);
            const openTime = Date.now();
            setImageModalOpenTime(openTime);

            // Countdown timer
            countdownInterval = setInterval(() => {
                setImageModalCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownInterval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            // Auto-close timeout
            imageModalTimeoutRef.current = setTimeout(() => {
                if (!pastedImage) {
                    console.log('No image provided, marking slot as Inactive/AFK:', currentSlot);
                    markSlotAsInactive();
                }
                closeImageModalAndContinue();
            }, 5 * 60 * 1000);
        }

        return () => {
            if (countdownInterval) clearInterval(countdownInterval);
            if (imageModalTimeoutRef.current) {
                clearTimeout(imageModalTimeoutRef.current);
            }
        };
    }, [showImageModal, currentSlot, pastedImage]);

    // Close image modal and continue to next cycle
    const closeImageModalAndContinue = () => {
        console.log('Closing image modal and continuing to next slot');
        setShowImageModal(false);
        setPastedImage(null);
        setImageModalOpenTime(null);
        setImageModalCountdown(3);

        const nextSlot = currentSlot + 1;
        setCurrentSlot(nextSlot);

        if (nextSlot > 10) {
            console.log('All slots completed, marking out');
            // markOut();
        } else {
            console.log('Adding new slot and starting cycle:', nextSlot);
            addNewProductivitySlot(nextSlot);
            setTimeout(() => {
                console.log('Starting normal cycle for slot:', nextSlot);
                startNormalCycle();
            }, 1000);
        }
    };

    // Load state from localStorage
    const loadPersistedState = () => {
        try {
            const persistedData = localStorage.getItem('energySoundState');
            const uploadStatus = localStorage.getItem('sessionUploadStatus');

            if (persistedData) {
                const data = JSON.parse(persistedData);
                setHasFirstStop(data.hasFirstStop || false);
                setStopLogs(data.stopLogs || []);
                setSessionStartTime(data.sessionStartTime || Date.now());
                setCurrentSlot(data.currentSlot || 1);
                setProductivitySlots(data.productivitySlots || []);
                setIsMarkedOut(data.isMarkedOut || false);
                setLastLogTime(data.lastLogTime || null);

                if (uploadStatus) {
                    const uploadData = JSON.parse(uploadStatus);
                    if (uploadData.uploaded) {
                        setIsMarkedOut(true);
                        return;
                    }
                }
                console.log('Loaded persisted state:', data);
            } else {
                setSessionStartTime(Date.now());
                initializeProductivitySlots();
                console.log('No persisted state, initialized new session');
            }
        } catch (error) {
            console.error('Error loading persisted state:', error);
            setSessionStartTime(Date.now());
            initializeProductivitySlots();
        }
    };

    // Initialize productivity slots - create all 10 slots at once
    const initializeProductivitySlots = () => {
        const slots = [];
        for (let i = 1; i <= 10; i++) {
            slots.push({
                hourSlot: i,
                status: 'Pending',
                timestamp: null,
                screenshot: null,
                activity: null,
                lastActivity: null
            });
        }
        setProductivitySlots(slots);
        console.log('Initialized 10 productivity slots');
    };

    // Add new productivity slot when needed
    const addNewProductivitySlot = (slotNumber) => {
        if (slotNumber > 10) return;

        setProductivitySlots(prev => {
            const exists = prev.some(slot => slot.hourSlot === slotNumber);
            if (!exists) {
                const newSlot = {
                    hourSlot: slotNumber,
                    status: 'Pending',
                    timestamp: null,
                    screenshot: null,
                    activity: null,
                    lastActivity: null
                };
                console.log('Added new productivity slot:', slotNumber);
                return [...prev, newSlot].sort((a, b) => a.hourSlot - b.hourSlot);
            }
            return prev;
        });
    };

    // Mark current slot as inactive
    const markSlotAsInactive = () => {
        const now = Date.now();
        setProductivitySlots(prev =>
            prev.map(slot =>
                slot.hourSlot === currentSlot
                    ? {
                        ...slot,
                        status: 'Inactive/AFK',
                        timestamp: new Date().toISOString(),
                        activity: 'No screenshot provided within 3 seconds',
                        lastActivity: now
                    }
                    : slot
            )
        );
        handleNotifyTechnician('Inactive/AFK')
        handleReminderForTechnician('Missed')
        showMessage.warning(`Slot ${currentSlot} marked as Inactive/AFK - No screenshot provided`);
    };

    // Save state to localStorage
    const savePersistedState = () => {
        try {
            const stateToSave = {
                hasFirstStop,
                stopLogs,
                sessionStartTime,
                currentSlot,
                productivitySlots,
                imageModalOpenTime,
                isMarkedOut,
                lastLogTime
            };
            localStorage.setItem('energySoundState', JSON.stringify(stateToSave));
            console.log('Saved state to localStorage');
        } catch (error) {
            console.error('Error saving state:', error);
        }
    };

    // Clear all intervals
    const clearAllIntervals = () => {
        clearTimeout(startTimeoutRef.current);
        clearTimeout(imageModalTimeoutRef.current);
        clearTimeout(autoStopTimeoutRef.current);
        clearTimeout(delayedModalTimeoutRef.current);
        clearTimeout(nextCycleTimeoutRef.current);
        console.log('Cleared all timeouts');
    };

    // Save state whenever relevant state changes
    useEffect(() => {
        savePersistedState();
    }, [hasFirstStop, stopLogs, sessionStartTime, currentSlot, productivitySlots, imageModalOpenTime, isMarkedOut, lastLogTime]);

    // Process and store image
    // const processImage = (file, type = 'uploaded') => {
    //     const reader = new FileReader();
    //     reader.onload = (event) => {
    //         const now = Date.now();
    //         const imageData = {
    //             id: now,
    //             data: event.target.result,
    //             timestamp: new Date().toISOString(),
    //             type: type,
    //             hourSlot: currentSlot,
    //             uploaded: false
    //         };

    //         storeImage(imageData);

    //         setProductivitySlots(prev =>
    //             prev.map(slot =>
    //                 slot.hourSlot === currentSlot
    //                     ? {
    //                         ...slot,
    //                         status: 'Active',
    //                         timestamp: new Date().toISOString(),
    //                         screenshot: imageData.id,
    //                         activity: `Screenshot ${type} successfully`,
    //                         lastActivity: now
    //                     }
    //                     : slot
    //             )
    //         );

    //         if (imageModalTimeoutRef.current) {
    //             clearTimeout(imageModalTimeoutRef.current);
    //         }
    //         handleNotifyTechnician('Active')
    //         message.success(`Screenshot for slot ${currentSlot} ${type} successfully!`);
    //         console.log(`Processed ${type} image for slot: ${currentSlot}`);

    //         setTimeout(() => {
    //             closeImageModalAndContinue();
    //         }, 500);
    //     };
    //     reader.readAsDataURL(file);
    // };
    const processImage = (file, type = 'uploaded') => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            canvas.toBlob((compressedBlob) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const now = Date.now();
                    const imageData = {
                        id: now,
                        data: event.target.result, // This is now compressed base64
                        timestamp: new Date().toISOString(),
                        type: type,
                        hourSlot: currentSlot,
                        uploaded: false
                    };

                    storeImage(imageData);

                    setProductivitySlots(prev =>
                        prev.map(slot =>
                            slot.hourSlot === currentSlot
                                ? {
                                    ...slot,
                                    status: 'Active',
                                    timestamp: new Date().toISOString(),
                                    screenshot: imageData.id,
                                    activity: `Screenshot ${type} successfully`,
                                    lastActivity: now
                                }
                                : slot
                        )
                    );

                    if (imageModalTimeoutRef.current) {
                        clearTimeout(imageModalTimeoutRef.current);
                    }
                    handleNotifyTechnician('Active')
                    showMessage.success(`Screenshot for slot ${currentSlot} ${type} successfully!`);
                    console.log(`Processed ${type} image for slot: ${currentSlot}`);

                    setTimeout(() => {
                        closeImageModalAndContinue();
                    }, 500);
                };
                reader.readAsDataURL(compressedBlob);
            }, 'image/jpeg', 0.8); // Compress to 80% quality
        };

        // Handle both File objects and pasted images
        if (file instanceof File) {
            const reader = new FileReader();
            reader.onload = (e) => {
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        } else {
            img.src = URL.createObjectURL(file);
        }
    };

    // Store image in localStorage
    const storeImage = (imageData) => {
        const existingImages = getStoredImages();
        const updatedImages = [...existingImages, imageData];
        localStorage.setItem('shiftScreenshots', JSON.stringify(updatedImages));
        console.log('Stored image in localStorage for slot:', imageData.hourSlot);
    };

    // Get stored images from localStorage
    const getStoredImages = () => {
        try {
            const stored = localStorage.getItem('shiftScreenshots');
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('Error reading stored images:', error);
            return [];
        }
    };



    // Start normal cycle for screenshot prompts - every 5 seconds for alert
    // const startNormalCycle = () => {

    //     console.log('Scheduling next cycle for slot:', currentSlot);
    //     if (nextCycleTimeoutRef.current) {
    //         clearTimeout(nextCycleTimeoutRef.current);
    //     }
    //     let delay = 3600000; // 1 hour3000; // 5 minutes;
    //     // let delay = 5000; // 1 hour3000; // 5 minutes;
    //     if (!hasFirstStop && currentSlot === 1) {
    //         delay = 3000;
    //     } else {
    //         delay = 3600000; // 5 minutes;
    //         // delay = 5000; // 5 minutes;
    //     }

    //     nextCycleTimeoutRef.current = setTimeout(() => {
    //         if (!isMarkedOut && currentSlot) {
    //             // Check if it's the very first cycle
    //             if (!hasFirstStop && currentSlot === 1) {
    //                 // debugger
    //                 console.log('First cycle: Skipping audio modal, going directly to image modal');
    //                 setHasFirstStop(true);
    //                 addNewProductivitySlot(currentSlot);

    //                 // ADD THE LOG CREATION CODE HERE:
    //                 const newLog = {
    //                     key: stopLogs.length + 1,
    //                     type: 'First Cycle',
    //                     stoppedAt: new Date().toLocaleTimeString(),
    //                     timestamp: new Date().toISOString(),
    //                     duration: '0s',
    //                     timeSinceLastLog: formatDuration(0),
    //                     hourSlot: currentSlot,
    //                     rawDuration: 0,
    //                     logTime: Date.now()
    //                 };

    //                 setStopLogs(prev => [newLog, ...prev]);
    //                 setLastLogTime(Date.now());
    //                 // END OF LOG CREATION CODE
    //                     setShowImageModal(true); // false
    //             } else {
    //                 console.log('Starting normal cycle with audio for slot:', currentSlot);
    //                 handlePlay('Auto');
    //             }
    //         } else {
    //             console.log('Cycle skipped due to conditions:', { isMarkedOut, currentSlot });
    //             setTimeout(startNormalCycle, 1000);
    //         }
    //     }, delay);
    // };

    const startNormalCycle = () => {
        console.log('Scheduling next cycle for slot:', currentSlot);

        if (nextCycleTimeoutRef.current) {
            clearTimeout(nextCycleTimeoutRef.current);
        }

        let delay = (!hasFirstStop && currentSlot === 1) ? 3000 : 3600000; // 3s first, 1h after

        nextCycleTimeoutRef.current = setTimeout(() => {
            // Skip if user is marked out or slot invalid
            if (isMarkedOut || !currentSlot) {
                console.log('Cycle skipped due to conditions:', { isMarkedOut, currentSlot });
                setTimeout(startNormalCycle, 1000);
                return; // stop here
            }

            // 🔹 FIRST CYCLE
            if (!hasFirstStop && currentSlot === 1) {
                console.log('First cycle: Skipping audio modal, going directly to image modal');
                setHasFirstStop(true);
                addNewProductivitySlot(currentSlot);

                const newLog = {
                    key: stopLogs.length + 1,
                    type: 'First Cycle',
                    stoppedAt: new Date().toLocaleTimeString(),
                    timestamp: new Date().toISOString(),
                    duration: '0s',
                    timeSinceLastLog: formatDuration(0),
                    hourSlot: currentSlot,
                    rawDuration: 0,
                    logTime: Date.now()
                };

                setStopLogs(prev => [newLog, ...prev]);
                setLastLogTime(Date.now());
                setShowImageModal(true);
                return; // done for first run
            }

            // 🔹 NORMAL CYCLE
            console.log('Starting normal cycle with audio for slot:', currentSlot);
            handlePlay('Auto');

            // ✅ Schedule next hourly cycle immediately (don’t wait for modal close)
            if (nextCycleTimeoutRef.current) {
                clearTimeout(nextCycleTimeoutRef.current);
            }
            nextCycleTimeoutRef.current = setTimeout(() => {
                console.log('🕒 Starting next hourly cycle automatically');
                startNormalCycle();
            }, 3600000); // exact 1 hour
        }, delay);
    };


    // Unlock audio and start sound system (for manual start button)
    const initializeAudio = async () => {
        if (isMarkedOut || currentSlot > 10) return;

        const audio = audioRef.current;
        if (!audio) return;

        try {
            audio.muted = true;
            await audio.play();
            audio.pause();
            audio.muted = false;
            console.log('Audio unlocked, starting first cycle');
            startNormalCycle();
        } catch (err) {
            console.warn('🔒 Failed to unlock audio:', err);
            startNormalCycle(); // Proceed with cycle even if audio fails
        }
    };


    const handleReminderForTechnician = async (status) => {
        try {
            const now = new Date();
            const dateOptions = {
                timeZone: 'Asia/Karachi',
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            };
            const timeOptions = {
                timeZone: 'Asia/Karachi',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            };
            const dateStr = now.toLocaleDateString('en-US', dateOptions);
            const timeStr = now.toLocaleTimeString('en-US', timeOptions).replace(' AM', ' AM').replace(' PM', ' PM');
            const fullTimestamp = `${dateStr} – ${timeStr}`;

            await axios.post(
                "/technician-slack-reminder",
                {
                    adminId: currentAdmin._id,
                    name: `${currentAdmin.name} ${currentAdmin.surname}`,
                    email: currentAdmin.email,
                    slot: currentSlot,
                    status: status,
                    timestamp: fullTimestamp,
                },

            );
            console.log("Technician reminded successfully");
        } catch (err) {
            console.error("Error reminding technician:", err);
        }
    }

    useEffect(() => {
        // ✅ only run when currentSlot changes
        if (currentSlot !== 1 && !showImageModal && !showModal) {
            let fiveMinBefore = 3300000;
            reminderTimeoutRef.current = setTimeout(() => {
                if (!isMarkedOut && currentSlot <= 10) {
                    const prevSlotNum = currentSlot - 1;
                    const prevSlot = productivitySlots.find(slot => slot.hourSlot === prevSlotNum);

                    console.log(
                        "Sending pre-reminder 5 minutes before prompt for slot:",
                        currentSlot
                    );

                    handleReminderForTechnician("Reminder");
                }
            }, fiveMinBefore);
        }

        // ✅ cleanup timeout on slot change/unmount
        return () => {
            if (reminderTimeoutRef.current) {
                clearTimeout(reminderTimeoutRef.current);
                reminderTimeoutRef.current = null;
            }
        };
    }, [currentSlot, showImageModal, showModal, isMarkedOut, productivitySlots]);
    // Auto-stop the modal after 5 seconds
    useEffect(() => {
        if (isPlaying && showModal) {
            autoStopTimeoutRef.current = setTimeout(() => {
                console.log('Auto-stopping alert for slot:', currentSlot);
                handleStop('Auto');
            }, 40000);
        }

        return () => {
            if (autoStopTimeoutRef.current) {
                clearTimeout(autoStopTimeoutRef.current);
            }
        };
    }, [isPlaying, showModal]);

    const handlePlay = (type = 'Auto') => {
        if (isMarkedOut || currentSlot > 10) {
            console.log('handlePlay blocked:', { isMarkedOut, currentSlot, showImageModal, showModal });
            return;
        }

        const now = Date.now();
        playStartTime.current = now;
        localStorage.setItem("playStartTime", now.toString());
        setIsPlaying(true);
        setShowModal(true);
        setElapsed(0);

        const audio = audioRef.current;
        if (audio) {
            audio.currentTime = 0;
            audio.volume = 1.0;
            audio.loop = true;
            audio.play().catch(err => {
                console.warn('Audio play failed:', err);
                setIsPlaying(false);
                setShowModal(false);
                startNormalCycle();
            });
        }
        console.log('Alert started for slot:', currentSlot);
    };

    // Format duration properly
    const formatDuration = (milliseconds) => {
        const totalSeconds = Math.floor(milliseconds / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (hours > 0) {
            return `${hours}h ${minutes}m ${seconds}s`;
        } else if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
        } else {
            return `${seconds}s`;
        }
    };

    // Calculate time since last log
    const getTimeSinceLastLog = (currentTime) => {
        if (!lastLogTime) return formatDuration(0);
        return formatDuration(currentTime - lastLogTime);
    };
    const handleNotifyTechnician = async (status) => {
        try {

            await axios.post(
                "/notify-technician",
                {
                    adminId: currentAdmin._id,
                    name: `${currentAdmin.name} ${currentAdmin.surname}`,
                    email: currentAdmin.email,
                    slot: currentSlot,
                    status: status,
                    // Force Asia/Karachi time directly in frontend
                    time: new Date().toLocaleTimeString("en-US", {
                        timeZone: "Asia/Karachi",
                    }),
                },
                {
                    headers: {
                        Authorization: `Bearer ${currentAdmin.token}`,
                    },
                }
            );
            console.log("Technician notified successfully");
        } catch (err) {
            console.error("Error notifying technician:", err);
        }
    }
    const handleStop = async (type = 'Manual') => {
        const audio = audioRef.current;

        if (audio && isPlaying) {
            audio.pause();

            const startTime = playStartTime.current || parseInt(localStorage.getItem("playStartTime"), 10) || Date.now();
            const endTime = Date.now();
            const duration = endTime - startTime;

            localStorage.removeItem("playStartTime");
            playStartTime.current = null;

            const newLog = {
                key: stopLogs.length + 1,
                type,
                stoppedAt: new Date().toLocaleTimeString(),
                timestamp: new Date().toISOString(),
                duration: formatDuration(duration),
                timeSinceLastLog: getTimeSinceLastLog(endTime),
                hourSlot: currentSlot,
                rawDuration: duration,
                logTime: endTime
            };

            setStopLogs(prev => [newLog, ...prev]);
            setLastLogTime(endTime);

            setIsPlaying(false);
            setShowModal(false);

            if (autoStopTimeoutRef.current) {
                clearTimeout(autoStopTimeoutRef.current);
            }
            // ✅ Send Slack notification trigger


            if ((type === 'Manual' || type === 'Auto') && currentSlot <= 10 && !isMarkedOut) {
                setHasFirstStop(true);
                addNewProductivitySlot(currentSlot);

                if (delayedModalTimeoutRef.current) {
                    clearTimeout(delayedModalTimeoutRef.current);
                }
                delayedModalTimeoutRef.current = setTimeout(() => {
                    console.log('Opening image modal for slot:', currentSlot);
                    setShowImageModal(true);
                }, 300);

            } else {
                setTimeout(startNormalCycle, 1000);
            }


            console.log('Alert stopped for slot:', currentSlot, 'Type:', type);
        }
    };

    // Update elapsed time
    useEffect(() => {
        let timer = null;
        if (isPlaying && playStartTime.current) {
            timer = setInterval(() => {
                setElapsed(Math.floor((Date.now() - playStartTime.current) / 1000));
            }, 1000);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [isPlaying]);

    // Handle file upload
    const uploadProps = {
        name: 'file',
        multiple: false,
        accept: 'image/*',
        beforeUpload: (file) => {
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
                showMessage.error('Please upload an image file!');
                return false;
            }
            console.log('Uploading image for slot:', currentSlot);
            processImage(file, 'uploaded');
            return false;
        },
        showUploadList: false,
    };

    const columns = [
        { title: 'Type', dataIndex: 'type', key: 'type' },
        { title: 'Slot', dataIndex: 'hourSlot', key: 'hourSlot' },
        { title: 'Stopped At', dataIndex: 'stoppedAt', key: 'stoppedAt' },
        { title: 'Alert Duration', dataIndex: 'duration', key: 'duration' },
        { title: 'Time Since Last', dataIndex: 'timeSinceLastLog', key: 'timeSinceLastLog' },
    ];

    const productivityColumns = [
        { title: 'Slot', dataIndex: 'hourSlot', key: 'hourSlot' },
        {
            title: 'Status', dataIndex: 'status', key: 'status',
            render: (status) => (
                <Text type={status === 'Active' ? 'success' : status === 'Inactive/AFK' ? 'danger' : 'warning'}>
                    {status}
                </Text>
            )
        },
        { title: 'Activity', dataIndex: 'activity', key: 'activity' },
        {
            title: 'Timestamp', dataIndex: 'timestamp', key: 'timestamp',
            render: (timestamp) => timestamp ? new Date(timestamp).toLocaleTimeString() : '-'
        },
    ];

    const storedImages = getStoredImages();
    const totalSlots = 10;
    const completedSlots = productivitySlots.filter(slot => slot.status !== 'Pending').length;
    const completionPercentage = (completedSlots / totalSlots) * 100;
    const activeSlots = productivitySlots.filter(slot => slot.status === 'Active').length;
    const inactiveSlots = productivitySlots.filter(slot => slot.status === 'Inactive/AFK').length;
    const pendingSlots = productivitySlots.filter(slot => slot.status === 'Pending').length;

    if (isMarkedOut) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Title level={2}>Session Completed</Title>
                <Text>Your productivity tracking session has ended and data has been uploaded.</Text>
                <div style={{ marginTop: '20px' }}>
                    <Text>Total Slots Completed: {completedSlots}/10</Text><br />
                    <Text type="success">Active Slots: {activeSlots}</Text><br />
                    <Text type="danger">Inactive/AFK Slots: {inactiveSlots}</Text><br />
                    <Text>Total Alert Stops: {stopLogs.length}</Text>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* <audio ref={audioRef} src="/logo_pianomotivacional_dm-257460.mp3" /> */}
            <audio ref={audioRef} src="/alarm-327234.mp3" />

            {!hasFirstStop && !isPlaying && sessionStartTime && (Date.now() - sessionStartTime) > 3000 && (
                <Button
                    type="primary"
                    icon={<SoundOutlined />}
                    onClick={initializeAudio}
                    style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}
                >
                    Start Productivity Tracking
                </Button>
            )}

            {/* {hasFirstStop && (
                <Button
                    type="danger"
                    // onClick={markOut}
                    style={{ position: 'fixed', bottom: 20, left: 20, zIndex: 1000 }}
                >
                    Mark Out & End Session
                </Button>
            )} */}

            <Modal
                title={
                    <div style={{ textAlign: 'center' }}>
                        <SoundOutlined style={{ fontSize: '24px', color: '#ff4d4f', marginRight: 8 }} />
                        <span>Productivity Alert System</span>
                    </div>
                }
                open={showModal}
                closable={false}
                maskClosable={false}
                footer={null}
                width={700}
                centered
                destroyOnClose={false}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Progress percent={(elapsed / 40) * 100} showInfo={false} />
                    {isPlaying ? (
                        <Text type="success" style={{ fontSize: '16px', textAlign: 'center', display: 'block' }}>
                            🔊 Playing... Elapsed: {elapsed}s / 40s (Auto-stop in {40 - elapsed}s)
                        </Text>
                    ) : (
                        <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                            ⏳ Waiting for next screenshot prompt...
                        </Text>
                    )}

                    <div style={{ textAlign: 'center', marginTop: 20 }}>
                        <Button
                            type="primary"
                            icon={<PauseOutlined />}
                            danger
                            size="large"
                            onClick={() => handleStop('Manual')}
                            disabled={!isPlaying}
                        >
                            Stop Alert
                        </Button>
                    </div>

                    {hasFirstStop && (
                        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6f6f6', borderRadius: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                <Text strong>📊 Productivity Overview:</Text>
                                <Text type="secondary">Current Slot: {currentSlot}</Text>
                            </div>
                            <Progress percent={completionPercentage} size="small" status="active" />
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                                <Text type="success">Active: {activeSlots}</Text>
                                <Text type="danger">Inactive/AFK: {inactiveSlots}</Text>
                                <Text type="warning">Pending: {pendingSlots}</Text>
                            </div>
                            <div style={{ marginTop: 8, textAlign: 'center' }}>
                                <Text strong>Total Alert Stops: {stopLogs.length}</Text>
                            </div>
                        </div>
                    )}

                    {productivitySlots.length > 0 && (
                        <div style={{ marginTop: 20, overflowY: 'auto', maxHeight: '200px' }}>
                            <Title level={5}>⏰ Productivity Status</Title>
                            <Table
                                dataSource={productivitySlots}
                                columns={productivityColumns}
                                pagination={false}
                                size="small"
                                rowKey="hourSlot"
                            />
                        </div>
                    )}

                    {stopLogs.length > 0 && (
                        <div style={{ marginTop: 20, overflowY: 'auto', maxHeight: '200px' }}>
                            <Title level={5}>🔇 Recent Alert Stops ({stopLogs.length} total)</Title>
                            <Table
                                dataSource={stopLogs}
                                columns={columns}
                                pagination={false}
                                size="small"
                                rowKey="key"
                            />
                        </div>
                    )}
                </Space>
            </Modal>

            <Modal
                title={
                    <div style={{ textAlign: 'center' }}>
                        <CameraOutlined style={{ fontSize: '24px', color: '#1890ff', marginRight: 8 }} />
                        <span>Slot {currentSlot} - Screenshot Required</span>
                        <ClockCircleOutlined style={{ fontSize: '16px', color: '#ff4d4f', marginLeft: 8 }} />
                    </div>
                }
                open={showImageModal}
                closable={false}
                maskClosable={false}
                footer={null}
                width={600}
                centered
                destroyOnClose={false}
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Text style={{ fontSize: '16px', display: 'block', marginBottom: '10px' }}>
                        Please provide a screenshot for slot {currentSlot} of your session.
                    </Text>

                    <Text type="secondary" style={{ display: 'block', marginBottom: '20px' }}>
                        Progress: {completedSlots}/{totalSlots} slots completed | {stopLogs.length} alert stops
                    </Text>

                    {/* <Text type="warning" style={{ display: 'block', fontSize: '14px', marginBottom: '20px' }}>
                        ⏰ Auto-closing in {imageModalCountdown} seconds (slot will be marked as Inactive/AFK if no screenshot)
                    </Text>
                     */}

                    <div style={{
                        backgroundColor: '#fff7e6',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '2px solid #ffa940'
                    }}>
                        <ClockCircleOutlined style={{ fontSize: '32px', color: '#ff4d4f', marginBottom: '10px' }} />
                        <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#ff4d4f', fontFamily: 'monospace' }}>
                            {(() => {
                                const minutes = Math.floor(imageModalCountdown / 60);
                                const seconds = imageModalCountdown % 60;
                                return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                            })()}
                        </div>
                        <Text type="warning" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                            Time remaining before auto-close
                        </Text>
                    </div>

                    {pastedImage && (
                        <div style={{ marginBottom: '20px' }}>
                            <Text type="success">📋 Image pasted! Processing...</Text>
                            <img
                                src={pastedImage}
                                alt="Pasted screenshot"
                                style={{ maxWidth: '200px', maxHeight: '150px', marginTop: '10px', border: '2px solid #52c41a' }}
                            />
                        </div>
                    )}

                    <Dragger {...uploadProps} style={{ marginBottom: '20px' }}>
                        <p className="ant-upload-drag-icon">
                            <UploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                        </p>
                        <p className="ant-upload-text">Click or drag image to upload</p>
                        <p className="ant-upload-hint">
                            Support for single image upload. PNG, JPG, GIF files are supported.
                        </p>
                    </Dragger>

                    <div style={{ backgroundColor: '#f0f0f0', padding: '15px', borderRadius: '8px', marginBottom: '10px' }}>
                        <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                            💡 Quick Paste Options:
                        </Text>
                        <Text type="secondary" style={{ display: 'block', fontSize: '14px' }}>
                            • Press <strong>Ctrl+V</strong> (or <strong>Cmd+V</strong> on Mac) to paste from clipboard
                        </Text>
                        <Text type="secondary" style={{ display: 'block', fontSize: '14px' }}>
                            • Take a screenshot and paste it directly here
                        </Text>
                        <Text type="secondary" style={{ display: 'block', fontSize: '14px' }}>
                            • Copy any image and paste it using keyboard shortcut
                        </Text>
                    </div>

                    <Text type="danger" style={{ display: 'block', fontSize: '12px', fontWeight: 'bold' }}>
                        ⚠️ This modal will auto-close in 5 minutes and mark the slot as Inactive/AFK if no screenshot is provided
                    </Text>
                </div>
            </Modal>
        </>
    );
};

export default GlobalEnergyModal;